import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import StickyNavigation from '@/components/layout/StickyNavigation';
import TenantFooter from '@/components/layout/TenantFooter';


// Build-time configured base for backend functions (defaults to Netlify path)
const FN_BASE = import.meta.env.VITE_FUNCTIONS_BASE || '/.netlify/functions';
const api = (p: string) => `${FN_BASE.replace(/\/$/, '')}/${p.replace(/^\//, '')}`;

type Step = 1 | 2 | 3 | 4 | 5 | 6 | 7;
// Step 1: Business type
// Step 2: Company details
// Step 3: Contents slider
// Step 4: Liability slider
// Step 5: Workers comp slider
// Step 6: Price/Review
// Step 7: Confirmation

export default function SMEPackageQuote() {
  const [step, setStep] = useState<Step>(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 1
  const [businessType, setBusinessType] = useState<'office' | 'retail' | 'fnb'>('office');

  // Step 2 (essentials + covers)
  const [companyName, setCompanyName] = useState<string>('My Company');
  const [contentsSum, setContentsSum] = useState<number>(100000);
  const [publicLiability, setPublicLiability] = useState<number>(5000000);
  const [annualWages, setAnnualWages] = useState<number>(50000);
  const [emirate, setEmirate] = useState<string>('Dubai');

  // Premium and breakdown
  const [quoteId, setQuoteId] = useState<string>('');
  const [premium, setPremium] = useState<number | null>(null);
  const [currency, setCurrency] = useState<string>('AED');
  const [priceBreakdown, setPriceBreakdown] = useState<{
    contents: number;
    liability: number;
    workersComp: number;
    subtotal: number;
    tax: number;
    total: number;
  } | null>(null);

  // Step 3 (contact)
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  const navigate = useNavigate();

  // Scroll to top when step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  const disabledNext1 = useMemo(() => !businessType, [businessType]);
  const disabledNext2 = useMemo(() => !companyName || contentsSum <= 0 || annualWages <= 0 || !emirate, [companyName, contentsSum, annualWages, emirate]);
  const disabledBuy = useMemo(() => !name || !email || !phone || !quoteId || premium == null, [name, email, phone, quoteId, premium]);

  // Enable mock mode for local development (set to true to skip API calls)
  const MOCK_MODE = true;

  // Loading screen state
  const [showingLoader, setShowingLoader] = useState(false);

  async function getPrice() {
    setLoading(true);
    setShowingLoader(true);
    setError(null);

    try {
      if (MOCK_MODE) {
        // Show loading screen for 2.5 seconds
        await new Promise(r => setTimeout(r, 2500));

        // Calculate individual coverage prices
        const contentsPremium = Math.round(contentsSum * 0.003 * 100) / 100; // 0.3% rate
        const liabilityPremium = publicLiability >= 25000000 ? 450 : publicLiability >= 10000000 ? 300 : 200;
        const workersCompPremium = Math.round(annualWages * 0.012 * 100) / 100; // 1.2% rate

        const subtotal = contentsPremium + liabilityPremium + workersCompPremium;
        const tax = Math.round(subtotal * 0.05 * 100) / 100; // 5% VAT
        const total = Math.round((subtotal + tax) * 100) / 100;

        setPriceBreakdown({
          contents: contentsPremium,
          liability: liabilityPremium,
          workersComp: workersCompPremium,
          subtotal,
          tax,
          total,
        });
        setQuoteId(`MOCK-${Date.now()}`);
        setPremium(total);
        setCurrency('AED');
        setShowingLoader(false);
        setStep(6 as Step);
        return;
      }

      // Real API call
      const submitRes = await fetch(api('aig-quote-submit'), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          businessType,
          companyName,
          contentsSum,
          publicLiability,
          annualWages,
          emirate,
        }),
      });
      const submitJson = await submitRes.json();
      if (!submitRes.ok) throw new Error(submitJson?.error || 'Quote submit failed');

      const qid = submitJson.quoteId;
      if (!qid) throw new Error('Missing quoteId');
      setQuoteId(qid);

      const p = Number(submitJson.premium ?? submitJson.total);
      setPremium(isFinite(p) && p > 0 ? p : null);
      setCurrency(submitJson.currency || 'AED');

      if (!isFinite(p) || p <= 0) {
        throw new Error('Could not calculate premium. Please check your inputs.');
      }

      setStep(6 as Step);
    } catch (e: any) {
      setError(e?.message || 'Failed to get price');
    } finally {
      setLoading(false);
    }
  }

  async function buyNow() {
    setLoading(true); setError(null);
    try {
      // Bind
      const bindRes = await fetch(api('aig-bind'), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quoteId, contact: { name, email, phone } }),
      });
      const bindJson = await bindRes.json();
      if (!bindRes.ok) throw new Error(bindJson?.error || 'Bind failed');
      const policyId = bindJson.policyId || bindJson.raw?.policyId || bindJson.raw?.id;

      // Invoice
      const invRes = await fetch(api('aig-invoice'), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ policyId, quoteId, amount: premium, currency, email }),
      });
      const invJson = await invRes.json();
      if (!invRes.ok) throw new Error(invJson?.error || 'Invoice failed');
      const invoiceId = invJson.invoiceId || invJson.id || invJson.reference;

      // Verify
      const verifyRes = await fetch(api('aig-verify'), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invoiceId }),
      });
      const verifyJson = await verifyRes.json();
      if (!verifyRes.ok) throw new Error(verifyJson?.error || 'Verify failed');

      // Documents (fire-and-forget)
      fetch(api('aig-documents'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ policyId, quoteId, emailTo: email }) }).catch(() => { });

      // Navigate to confirmation
      navigate('/confirmation', { state: { policyId, premium, currency } });
      setStep(4);
    } catch (e: any) {
      setError(e?.message || 'Purchase failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <StickyNavigation />

      {/* Loading Screen */}
      {showingLoader && (
        <div className="fixed inset-0 bg-white z-50 flex flex-col items-center justify-center">
          <div className="text-center">
            {/* Spinning YallaCompare Logo */}
            <div className="flex justify-center mb-8">
              <img
                src="https://mborlyertmbornltlmdf.supabase.co/storage/v1/object/public/insurer-logos/yc%20small%20logo.png"
                alt="YallaCompare"
                className="w-24 h-24 animate-spin"
                style={{ animationDuration: '2s' }}
              />
            </div>

            <p className="text-xl font-medium text-gray-700 mb-2">Calculating your quote...</p>
            <p className="text-sm text-gray-500">Finding the best coverage for {companyName}</p>
          </div>
        </div>
      )}
      <div className="max-w-3xl mx-auto px-4 py-10">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Business Insurance Made Simple</h1>
          <p className="text-gray-600 text-lg">Protect your business in under 5 minutes. Get covered today.</p>
        </div>

        {error && (<div className="mb-4 p-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>)}

        {/* Progress dots */}
        <div className="flex items-center justify-center space-x-2 mb-8">
          {[1, 2, 3, 4, 5, 6, 7].map(s => (
            <div
              key={s}
              className={`w-3 h-3 rounded-full transition-all ${step >= s ? 'bg-blue-600' : 'bg-gray-300'} ${step === s ? 'w-4 h-4' : ''}`}
            />
          ))}
        </div>

        {step === 1 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-2xl">What type of business do you run?</CardTitle>
              <p className="text-gray-500 mt-1">We'll tailor your coverage to match your industry</p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button
                  onClick={() => setBusinessType('office')}
                  className={`border-2 rounded-xl p-6 text-left transition-all hover:border-blue-400 hover:shadow-md ${businessType === 'office' ? 'border-blue-600 bg-blue-50 shadow-md' : 'border-gray-200'}`}
                >
                  <div className="text-3xl mb-3">🏢</div>
                  <div className="font-semibold text-lg">Office & Professional</div>
                  <div className="text-sm text-gray-500 mt-2">
                    Consultants, accountants, lawyers, tech companies, agencies
                  </div>
                </button>

                <button
                  onClick={() => setBusinessType('retail')}
                  className={`border-2 rounded-xl p-6 text-left transition-all hover:border-blue-400 hover:shadow-md ${businessType === 'retail' ? 'border-blue-600 bg-blue-50 shadow-md' : 'border-gray-200'}`}
                >
                  <div className="text-3xl mb-3">🛒</div>
                  <div className="font-semibold text-lg">Retail & Trading</div>
                  <div className="text-sm text-gray-500 mt-2">
                    Shops, showrooms, e-commerce, wholesale, trading
                  </div>
                </button>

                <button
                  onClick={() => setBusinessType('fnb')}
                  className={`border-2 rounded-xl p-6 text-left transition-all hover:border-blue-400 hover:shadow-md ${businessType === 'fnb' ? 'border-blue-600 bg-blue-50 shadow-md' : 'border-gray-200'}`}
                >
                  <div className="text-3xl mb-3">🍽️</div>
                  <div className="font-semibold text-lg">Food & Beverage</div>
                  <div className="text-sm text-gray-500 mt-2">
                    Restaurants, cafes, catering, food trucks, bars
                  </div>
                </button>
              </div>
              <div className="hidden sm:flex justify-end mt-6">
                <Button disabled={disabledNext1} onClick={() => setStep(2)} className="px-8">
                  Continue →
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Company Details */}
        {step === 2 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-2xl">Tell us about your business</CardTitle>
              <p className="text-gray-500 mt-1">Just a couple of quick details</p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-4 bg-gray-50 rounded-lg">
                <Label htmlFor="companyName" className="text-base font-semibold">Company Name</Label>
                <Input id="companyName" value={companyName} onChange={e => setCompanyName(e.target.value)} className="mt-2" placeholder="Your registered company name" />
              </div>

              <div className="p-4 bg-gray-50 rounded-lg">
                <Label htmlFor="emirate" className="text-base font-semibold">Where is your business located?</Label>
                <select
                  id="emirate"
                  value={emirate}
                  onChange={e => setEmirate(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 bg-white mt-2"
                >
                  <option value="Dubai">Dubai</option>
                  <option value="Abu Dhabi">Abu Dhabi</option>
                  <option value="Sharjah">Sharjah</option>
                  <option value="Ajman">Ajman</option>
                  <option value="Ras Al Khaimah">Ras Al Khaimah</option>
                  <option value="Fujairah">Fujairah</option>
                  <option value="Umm Al Quwain">Umm Al Quwain</option>
                </select>
              </div>

              <div className="hidden sm:flex justify-between pt-4 border-t">
                <Button variant="secondary" onClick={() => setStep(1)}>← Back</Button>
                <Button disabled={!companyName} onClick={() => setStep(3)} className="px-8">
                  Continue →
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Contents Insurance Slider */}
        {step === 3 && (
          <Card className="mb-6">
            <CardHeader className="text-center">
              <div className="text-5xl mb-4">📦</div>
              <CardTitle className="text-2xl">Contents Protection</CardTitle>
              <p className="text-gray-500 mt-2 max-w-md mx-auto">
                Protects your stuff — furniture, computers, stock, and equipment. If it's stolen, damaged by fire, or flooded, you're covered.
              </p>
            </CardHeader>
            <CardContent>
              <div className="py-8">
                <div className="text-center mb-4">
                  <div className="text-sm text-gray-500 mb-1">How much is everything in your office/shop worth?</div>
                  <div className="text-4xl font-bold text-blue-600">
                    {contentsSum.toLocaleString()} AED
                  </div>
                </div>
                <input
                  type="range"
                  min="25000"
                  max="1000000"
                  step="25000"
                  value={contentsSum}
                  onChange={e => setContentsSum(Number(e.target.value))}
                  className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                />
                <div className="flex justify-between text-sm text-gray-400 mt-2">
                  <span>25K</span>
                  <span>500K</span>
                  <span>1M</span>
                </div>
              </div>
              <div className="hidden sm:flex justify-between pt-4 border-t">
                <Button variant="secondary" onClick={() => setStep(2)}>← Back</Button>
                <Button onClick={() => setStep(4)} className="px-8">Continue →</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Public Liability Slider */}
        {step === 4 && (
          <Card className="mb-6">
            <CardHeader className="text-center">
              <div className="text-5xl mb-4">🛡️</div>
              <CardTitle className="text-2xl">Public Liability</CardTitle>
              <p className="text-gray-500 mt-2 max-w-md mx-auto">
                If a customer slips in your shop or you accidentally damage someone's property, this covers legal costs and compensation.
              </p>
            </CardHeader>
            <CardContent>
              <div className="py-8">
                <div className="text-center mb-4">
                  <div className="text-sm text-gray-500 mb-1">Choose your coverage limit</div>
                  <div className="text-4xl font-bold text-blue-600">
                    {(publicLiability / 1000000).toLocaleString()}M AED
                  </div>
                </div>
                <input
                  type="range"
                  min="1000000"
                  max="25000000"
                  step="1000000"
                  value={publicLiability}
                  onChange={e => setPublicLiability(Number(e.target.value))}
                  className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                />
                <div className="flex justify-between text-sm text-gray-400 mt-2">
                  <span>1M</span>
                  <span>10M</span>
                  <span>25M</span>
                </div>
              </div>
              <div className="hidden sm:flex justify-between pt-4 border-t">
                <Button variant="secondary" onClick={() => setStep(3)}>← Back</Button>
                <Button onClick={() => setStep(5)} className="px-8">Continue →</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 5: Workers Compensation Slider */}
        {step === 5 && (
          <Card className="mb-6">
            <CardHeader className="text-center">
              <div className="text-5xl mb-4">👷</div>
              <CardTitle className="text-2xl">Employee Protection</CardTitle>
              <p className="text-gray-500 mt-2 max-w-md mx-auto">
                Covers your employees if they get injured at work. Required by UAE law if you have staff.
              </p>
            </CardHeader>
            <CardContent>
              <div className="py-8">
                <div className="text-center mb-4">
                  <div className="text-sm text-gray-500 mb-1">Total annual wages you pay to all staff</div>
                  <div className="text-4xl font-bold text-blue-600">
                    {annualWages.toLocaleString()} AED
                  </div>
                  <div className="text-sm text-gray-400">per year</div>
                </div>
                <input
                  type="range"
                  min="0"
                  max="20000000"
                  step="500000"
                  value={annualWages}
                  onChange={e => setAnnualWages(Number(e.target.value))}
                  className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                />
                <div className="flex justify-between text-sm text-gray-400 mt-2">
                  <span>0</span>
                  <span>10M</span>
                  <span>20M</span>
                </div>
              </div>
              <div className="hidden sm:flex justify-between pt-4 border-t">
                <Button variant="secondary" onClick={() => setStep(4)}>← Back</Button>
                <Button disabled={loading} onClick={getPrice} className="px-8">
                  {loading ? 'Calculating...' : 'Get My Price →'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === 6 && (
          <Card className="mb-6">
            <CardContent className="pt-8">
              {/* BIG PRICE FIRST */}
              {priceBreakdown && (
                <div className="text-center mb-8">
                  <p className="text-gray-500 text-sm mb-2">Your annual premium</p>
                  <div className="text-5xl sm:text-6xl font-bold text-gray-900">
                    {priceBreakdown.total.toLocaleString()} <span className="text-3xl">{currency}</span>
                  </div>
                </div>
              )}

              {/* Coverage Summary */}
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <div className="font-semibold mb-3">{companyName}</div>

                {priceBreakdown && (
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between py-2 border-b border-gray-200">
                      <span className="text-gray-600">Contents ({contentsSum.toLocaleString()} AED covered)</span>
                      <span className="font-medium">{priceBreakdown.contents.toLocaleString()} {currency}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-200">
                      <span className="text-gray-600">Liability ({(publicLiability / 1000000).toLocaleString()}M AED limit)</span>
                      <span className="font-medium">{priceBreakdown.liability.toLocaleString()} {currency}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-200">
                      <span className="text-gray-600">Workers Comp ({annualWages.toLocaleString()} AED payroll)</span>
                      <span className="font-medium">{priceBreakdown.workersComp.toLocaleString()} {currency}</span>
                    </div>
                    <div className="flex justify-between py-2 text-gray-500">
                      <span>Subtotal</span>
                      <span>{priceBreakdown.subtotal.toLocaleString()} {currency}</span>
                    </div>
                    <div className="flex justify-between py-2 text-gray-500">
                      <span>VAT (5%)</span>
                      <span>{priceBreakdown.tax.toLocaleString()} {currency}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Buy Now Button */}
              <div className="hidden sm:flex justify-between mt-4">
                <Button variant="secondary" onClick={() => setStep(5)}>← Back</Button>
                <Button onClick={() => setStep(7 as Step)} className="px-8">Buy Now</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 7: Contact Details */}
        {step === 7 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-2xl">Almost there!</CardTitle>
              <p className="text-gray-500 mt-1">Enter your contact details to complete your purchase</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="name">Full name</Label>
                  <Input id="name" value={name} onChange={e => setName(e.target.value)} className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input id="phone" value={phone} onChange={e => setPhone(e.target.value)} className="mt-1" />
                </div>
              </div>
              <div className="hidden sm:flex justify-between mt-6">
                <Button variant="secondary" onClick={() => setStep(6 as Step)}>← Back</Button>
                <Button disabled={!name || !email || !phone || loading} onClick={buyNow}>
                  {loading ? 'Processing…' : 'Confirm & Pay'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Fixed Mobile Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-4 sm:hidden z-50">
        <div className="flex justify-between gap-3 max-w-3xl mx-auto">
          {step > 1 ? (
            <Button
              variant="secondary"
              onClick={() => setStep((step - 1) as Step)}
              className="flex-1"
            >
              ← Back
            </Button>
          ) : (
            <div className="flex-1" />
          )}

          {step === 1 && (
            <Button
              disabled={disabledNext1}
              onClick={() => setStep(2)}
              className="flex-1"
            >
              Continue →
            </Button>
          )}
          {step === 2 && (
            <Button
              disabled={!companyName}
              onClick={() => setStep(3)}
              className="flex-1"
            >
              Continue →
            </Button>
          )}
          {step === 3 && (
            <Button onClick={() => setStep(4)} className="flex-1">
              Continue →
            </Button>
          )}
          {step === 4 && (
            <Button onClick={() => setStep(5)} className="flex-1">
              Continue →
            </Button>
          )}
          {step === 5 && (
            <Button
              disabled={loading}
              onClick={getPrice}
              className="flex-1"
            >
              {loading ? 'Calculating...' : 'Get My Price →'}
            </Button>
          )}
          {step === 6 && (
            <Button
              onClick={() => setStep(7 as Step)}
              className="flex-1"
            >
              Buy Now
            </Button>
          )}
          {step === 7 && (
            <Button
              disabled={!name || !email || !phone || loading}
              onClick={buyNow}
              className="flex-1"
            >
              {loading ? 'Processing…' : 'Confirm & Pay'}
            </Button>
          )}
        </div>
      </div>

      {/* Add padding at bottom for fixed nav on mobile */}
      <div className="h-20 sm:hidden" />

      <TenantFooter />
    </div>
  );
}

